// components/layout/Header.js
import React from 'react';
import { Button } from '../common';
import CustomerActionsNotifier from '../dashboard/CustomerActionsNotifier';
import { useBusinessConfig } from '../../context/BusinessConfigContext';

export function Header({
  title,
  subtitle,
  actions = [],
  breadcrumbs = [],
  className = '',
  centerComponent = null
}) {
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const { config } = useBusinessConfig();

  return (
    <header className={`bg-white border-b border-gray-200 px-6 py-3 ${className}`} style={{ backgroundColor: '#FAFAFA' }}>
      <div className="flex items-center justify-between">
        {/* Left side - Title and info */}
        <div className="flex-1">
          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <nav className="flex mb-2" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3">
                {breadcrumbs.map((crumb, index) => (
                  <li key={index} className="inline-flex items-center">
                    {index > 0 && (
                      <svg
                        className="w-4 h-4 text-gray-400 mx-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {crumb.href ? (
                      <button
                        onClick={crumb.onClick}
                        className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        {crumb.label}
                      </button>
                    ) : (
                      <span className="text-sm text-gray-900 font-medium">
                        {crumb.label}
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          )}

          {/* Title and subtitle */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              {title}
              {title === 'Good Morning, Sivan' && (
                <span className="ml-2">✨</span>
              )}
            </h1>
            {subtitle && (
              <p className="text-sm text-gray-600 mt-1">
                {subtitle}
              </p>
            )}
            {title.includes('Good Morning') && (
              <p className="text-sm text-gray-600 mt-1">
                {currentDate}
              </p>
            )}
          </div>
        </div>

        {/* Center component */}
        {centerComponent && (
          <div className="flex-1 flex items-center justify-center mx-4">
            {centerComponent}
          </div>
        )}

        {/* Right side - Actions */}
        {actions.length > 0 && (
          <div className="flex items-center space-x-3 gap-2">
            {actions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant || 'primary'}
                size={action.size || 'medium'}
                onClick={action.onClick}
                icon={action.icon}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}

        <div className="mr-4">
          {config?.settings?.approval_workflow_enabled && (
            <CustomerActionsNotifier />
          )}
        </div>
      </div>
    </header>
  );
}

// Page header with common patterns
export function PageHeader({
  title,
  description,
  primaryAction,
  secondaryActions = [],
  backAction
}) {
  return (
    <div className="bg-white border-b border-gray-200 px-6 py-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          {/* Back button */}
          {backAction && (
            <button
              onClick={backAction.onClick}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {backAction.label || 'Back'}
            </button>
          )}

          {/* Title and description */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {title}
            </h1>
            {description && (
              <p className="text-gray-600 mt-2 max-w-2xl">
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-3">
          {secondaryActions.map((action, index) => (
            <Button
              key={index}
              variant="secondary"
              size="medium"
              onClick={action.onClick}
              icon={action.icon}
            >
              {action.label}
            </Button>
          ))}

          {primaryAction && (
            <Button
              variant="primary"
              size="medium"
              onClick={primaryAction.onClick}
              icon={primaryAction.icon}
            >
              {primaryAction.label}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}